import type { Project } from '@plane/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { FotoDoProjeto } from '@/modules/projects/components/foto-do-projeto';
import {
  useDeleteProject,
  useProjects,
  useReorderProjects,
  useUpdateProject,
} from '@/modules/projects/hooks/use-projects';
import { ApiRequestError } from '@/services/http-client';

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

function Etiqueta({ publicado }: { publicado: boolean }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs ${
        publicado ? 'bg-brand-150 text-brand-950' : 'bg-sand text-ink-soft'
      }`}
    >
      {publicado ? 'Publicado' : 'Rascunho'}
    </span>
  );
}

function Miniatura({ projeto }: { projeto: Project }) {
  const capa = projeto.images[0];

  if (!capa) {
    return (
      <div className="flex h-16 w-24 shrink-0 items-center justify-center bg-sand text-xs text-ink-soft">
        sem foto
      </div>
    );
  }

  return (
    <FotoDoProjeto
      url={capa.url}
      alt={capa.alt}
      width={96}
      height={64}
      className="h-16 w-24 shrink-0 object-cover"
    />
  );
}

export function ProjectsListPage() {
  const consulta = useProjects();
  const atualizar = useUpdateProject();
  const remover = useDeleteProject();
  const reordenar = useReorderProjects();
  const [erro, setErro] = useState<string | null>(null);

  const ocupado = atualizar.isPending || remover.isPending || reordenar.isPending;

  async function executar(acao: () => Promise<unknown>) {
    setErro(null);

    try {
      await acao();
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    }
  }

  /**
   * Move um item e manda a ordem inteira.
   *
   * Botões em vez de arrastar: com poucos projetos resolve, funciona no teclado
   * e não pede biblioteca de drag and drop. Se a galeria crescer muito, aí sim
   * vale a pena reavaliar.
   */
  function mover(lista: Project[], indice: number, direcao: -1 | 1) {
    const destino = indice + direcao;

    if (destino < 0 || destino >= lista.length) return;

    const ids = lista.map((p) => p.id);
    const atual = ids[indice];
    const outro = ids[destino];

    if (!atual || !outro) return;

    ids[indice] = outro;
    ids[destino] = atual;

    void executar(() => reordenar.mutateAsync(ids));
  }

  const cabecalho = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl">Projetos</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Obras entregues, na ordem em que aparecem no site.
        </p>
      </div>

      <Link
        to="/projetos/novo"
        className="bg-brand-600 px-4 py-2.5 text-sm text-bone transition-colors hover:bg-brand-700"
      >
        Novo projeto
      </Link>
    </div>
  );

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {cabecalho}
        <p role="status" aria-live="polite" className="text-sm text-ink-soft">
          Carregando projetos…
        </p>
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="flex flex-col gap-6">
        {cabecalho}
        <div role="alert" className="border border-brass bg-bone-soft px-4 py-3 text-sm">
          <p>{mensagemDeErro(consulta.error)}</p>
          <button
            type="button"
            onClick={() => void consulta.refetch()}
            className="mt-2 text-brand-700 underline"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  const projetos = consulta.data;

  return (
    <div className="flex flex-col gap-6">
      {cabecalho}

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-4 py-3 text-sm">
          {erro}
        </p>
      )}

      {projetos.length === 0 ? (
        <div className="border border-clay px-4 py-8 text-center">
          <p className="text-sm text-ink-soft">Nenhum projeto cadastrado ainda.</p>
          <Link to="/projetos/novo" className="mt-2 inline-block text-sm text-brand-700 underline">
            Cadastrar o primeiro
          </Link>
        </div>
      ) : (
        <ul className="flex flex-col">
          {projetos.map((projeto, indice) => (
            <li
              key={projeto.id}
              className="flex items-center gap-4 border-b border-sand py-3 first:border-t"
            >
              <div className="flex flex-col" aria-label="Ordem">
                <button
                  type="button"
                  onClick={() => mover(projetos, indice, -1)}
                  disabled={ocupado || indice === 0}
                  aria-label={`Mover ${projeto.title} para cima`}
                  className="px-1 text-ink-soft hover:text-brand-700 disabled:opacity-30"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => mover(projetos, indice, 1)}
                  disabled={ocupado || indice === projetos.length - 1}
                  aria-label={`Mover ${projeto.title} para baixo`}
                  className="px-1 text-ink-soft hover:text-brand-700 disabled:opacity-30"
                >
                  ↓
                </button>
              </div>

              <Miniatura projeto={projeto} />

              <div className="min-w-0 flex-1">
                <p className="truncate">{projeto.title}</p>
                <p className="truncate text-sm text-ink-soft">
                  {projeto.place} · {projeto.year} · {projeto.images.length}{' '}
                  {projeto.images.length === 1 ? 'foto' : 'fotos'}
                </p>
              </div>

              <Etiqueta publicado={projeto.published} />

              <div className="shrink-0 text-right text-sm whitespace-nowrap">
                <Link to={`/projetos/${projeto.id}`} className="text-brand-700 underline">
                  Editar
                </Link>

                <button
                  type="button"
                  onClick={() =>
                    void executar(() =>
                      atualizar.mutateAsync({
                        id: projeto.id,
                        input: { published: !projeto.published },
                      }),
                    )
                  }
                  disabled={ocupado}
                  className="ml-4 text-brand-700 underline disabled:opacity-50"
                >
                  {projeto.published ? 'Despublicar' : 'Publicar'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Remover "${projeto.title}"? As fotos serão apagadas junto e não há como desfazer.`,
                      )
                    ) {
                      return;
                    }

                    void executar(() => remover.mutateAsync(projeto.id));
                  }}
                  disabled={ocupado}
                  className="ml-4 text-brass underline disabled:opacity-50"
                >
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
